import React, { useEffect, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const parseCoord = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const createLabeledIcon = (label, color) =>
  L.divIcon({
    className: 'order-map-marker',
    html: `
      <div style="display:flex;flex-direction:column;align-items:center;transform:translateY(-6px);">
        <div style="
          padding:2px 8px;margin-bottom:4px;border-radius:999px;
          background:#111827;color:#fff;font-size:11px;font-weight:600;
          white-space:nowrap;box-shadow:0 1px 4px rgba(0,0,0,.3);
        ">${label}</div>
        <div style="
          width:28px;height:28px;border-radius:50%;
          background:${color};border:3px solid #fff;
          box-shadow:0 2px 6px rgba(0,0,0,.3);
        "></div>
      </div>
    `,
    iconSize: [100, 48],
    iconAnchor: [50, 42],
    popupAnchor: [0, -36],
  });

const FitBounds = ({ points }) => {
  const map = useMap();
  useEffect(() => {
    if (!points?.length) return;
    if (points.length === 1) {
      map.setView(points[0], 15);
      return;
    }
    map.fitBounds(L.latLngBounds(points), { padding: [48, 48], maxZoom: 15 });
  }, [map, points]);
  return null;
};

/**
 * Shows PickupLAT/LNG and DeliveryLAT/LNG from the order on a map.
 */
const OrderDeliveryMap = ({ order, height = 420 }) => {
  const pickup = useMemo(() => {
    const lat = parseCoord(order?.PickupLAT);
    const lng = parseCoord(order?.PickupLNG);
    if (lat == null || lng == null || (lat === 0 && lng === 0)) return null;
    return { lat, lng };
  }, [order?.PickupLAT, order?.PickupLNG]);

  const delivery = useMemo(() => {
    const lat = parseCoord(order?.DeliveryLAT);
    const lng = parseCoord(order?.DeliveryLNG);
    if (lat == null || lng == null || (lat === 0 && lng === 0)) return null;
    return { lat, lng };
  }, [order?.DeliveryLAT, order?.DeliveryLNG]);

  const points = useMemo(() => {
    const list = [];
    if (pickup) list.push([pickup.lat, pickup.lng]);
    if (delivery) list.push([delivery.lat, delivery.lng]);
    return list;
  }, [pickup, delivery]);

  const center = points[0] || [25.2048, 55.2708];
  const hasPoints = points.length > 0;

  return (
    <div className="space-y-3">
      <style>{`
        .order-map-marker {
          background: transparent !important;
          border: none !important;
        }
      `}</style>

      <div className="flex flex-wrap gap-3 text-xs">
        {pickup ? (
          <span className="inline-flex items-center gap-2 rounded-full bg-blue-50 px-3 py-1 text-blue-800 border border-blue-100">
            <span className="h-2.5 w-2.5 rounded-full bg-blue-500" />
            Pickup: {pickup.lat.toFixed(6)}, {pickup.lng.toFixed(6)}
          </span>
        ) : (
          <span className="inline-flex items-center gap-2 rounded-full bg-gray-50 px-3 py-1 text-gray-500 border border-gray-200">
            Pickup GPS not set
          </span>
        )}
        {delivery ? (
          <span className="inline-flex items-center gap-2 rounded-full bg-emerald-50 px-3 py-1 text-emerald-800 border border-emerald-100">
            <span className="h-2.5 w-2.5 rounded-full bg-emerald-500" />
            Delivery: {delivery.lat.toFixed(6)}, {delivery.lng.toFixed(6)}
          </span>
        ) : (
          <span className="inline-flex items-center gap-2 rounded-full bg-gray-50 px-3 py-1 text-gray-500 border border-gray-200">
            Delivery GPS not set
          </span>
        )}
      </div>

      <div className="relative overflow-hidden rounded-xl border border-gray-200 shadow-sm" style={{ height }}>
        {!hasPoints && (
          <div className="pointer-events-none absolute left-1/2 top-3 z-[500] -translate-x-1/2 rounded-lg border border-amber-200 bg-amber-50/95 px-3 py-1.5 text-xs text-amber-800 shadow">
            No pickup/delivery GPS on this order yet
          </div>
        )}
        <MapContainer
          center={center}
          zoom={hasPoints ? 13 : 11}
          style={{ height: '100%', width: '100%' }}
          scrollWheelZoom
        >
          {hasPoints && <FitBounds points={points} />}
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            maxZoom={19}
          />

          {pickup && delivery && (
            <Polyline
              positions={[[pickup.lat, pickup.lng], [delivery.lat, delivery.lng]]}
              pathOptions={{ color: '#46955A', weight: 3, dashArray: '6 8', opacity: 0.85 }}
            />
          )}

          {pickup && (
            <Marker position={[pickup.lat, pickup.lng]} icon={createLabeledIcon('Pickup', '#3B82F6')}>
              <Popup>
                <div className="text-sm">
                  <p className="font-semibold">Pickup</p>
                  <p className="text-xs text-gray-600">
                    {pickup.lat.toFixed(6)}, {pickup.lng.toFixed(6)}
                  </p>
                </div>
              </Popup>
            </Marker>
          )}

          {delivery && (
            <Marker position={[delivery.lat, delivery.lng]} icon={createLabeledIcon('Delivery', '#10B981')}>
              <Popup>
                <div className="text-sm">
                  <p className="font-semibold">Delivery</p>
                  <p className="text-xs text-gray-600">
                    {delivery.lat.toFixed(6)}, {delivery.lng.toFixed(6)}
                  </p>
                </div>
              </Popup>
            </Marker>
          )}
        </MapContainer>
      </div>
    </div>
  );
};

export default OrderDeliveryMap;
